import isEqual from 'lodash/isEqual'
import { useState, useCallback, useEffect } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Card from '@mui/material/Card'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Unstable_Grid2'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Button from '@mui/material/Button'
import { Helmet } from 'react-helmet-async'

import { paths } from 'src/routes/paths'
import { useSettingsContext } from 'src/components/settings'
import Iconify from 'src/components/iconify'
import Datagrid from 'src/components/datagrid'
import { IOrderTableFilterValue, IOrderTableFilters } from 'src/types/orders'
import useOrders from 'src/service/orders.service'

import OrderTableFiltersResult from './table-user/orders-table-filters-result'
import OrderTableRow from './table-user/orders-table-row'
import OrderTableToolbar from './table-user/orders-table-toolbar'
import MetricCard from './metric-card'
import { formatCurrency } from './format-currency'
import useOrdersExport from './use-orders-export'

const TABLE_HEAD = [
  { id: 'number', label: 'Pedido', width: 90 },
  { id: 'event', label: 'Evento', width: 180 },
  { id: 'saler', label: 'Vendedor', width: 140 },
  { id: 'paymentType', label: 'Pagamento', width: 120 },
  { id: 'terminalSerial', label: 'Terminal', width: 100 },
  { id: 'total', label: 'Total', width: 100 },
  { id: 'tax', label: 'Taxa', width: 100 },
  { id: 'net', label: 'Líquido', width: 100 },
  { id: 'date', label: 'Data', width: 120 },
]

const defaultPagination = {
  page: 1,
  perPage: 8,
  sort: {
    createdAt: 'desc',
  },
}

const defaultFilters: IOrderTableFilters = {
  event: null,
  dateStart: null,
  dateEnd: null,
  categories: [],
  salers: [],
  paymentType: [],
  terminalSerial: '',
}

export default function OrdersListView() {
  const [pagination, setPagination] = useState(defaultPagination)
  const [filters, setFilters] = useState<IOrderTableFilters>(defaultFilters)
  const [autoRefresh, setAutoRefresh] = useState(false)

  const { isLoading, isFetching, data, refetch } = useOrders(pagination, filters)
  const { handleExportExcel, handleExportPDF } = useOrdersExport(data, filters)

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null
    if (autoRefresh) {
      interval = setInterval(() => refetch(), 30000)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [autoRefresh, refetch])

  const settings = useSettingsContext()
  const canReset = !isEqual(defaultFilters, filters)
  const notFound = (!data?.data.length && canReset) || !data?.data.length

  const handleFilters = useCallback((name: string, value: IOrderTableFilterValue) => {
    setFilters((prevState) => ({ ...prevState, [name]: value }))
  }, [])

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters)
  }, [])

  const handleRefresh = useCallback(() => {
    refetch()
  }, [refetch])

  const extraData = data?.extraData || {}
  const isLoadingData = isLoading || isFetching

  return (
    <>
      <Helmet>
        <title>Vendas - Dashboard</title>
      </Helmet>

      <Container maxWidth={settings.themeStretch ? false : 'xl'}>
        <DashboardHeader
          autoRefresh={autoRefresh}
          setAutoRefresh={setAutoRefresh}
          isLoadingData={isLoadingData}
          handleRefresh={handleRefresh}
          hasEvent={!!filters.event}
          data={data}
          extraData={extraData}
        />

        <Stack spacing={2.5}>
          <Card
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              gap={1.5}
              sx={{
                px: 2.5,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
              }}
            >
              <Stack direction='row' alignItems='center' gap={1.25}>
                <Iconify icon='solar:filter-bold' width={18} sx={{ color: 'text.secondary' }} />
                <Typography variant='subtitle2' fontWeight={700} color='text.primary'>
                  Filtros
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: { xs: 'none', md: 'inline' } }}
                >
                  ·{' '}
                  {filters.event
                    ? 'Refine sua busca'
                    : 'Selecione um evento para visualizar as vendas'}
                </Typography>
              </Stack>
              {canReset && (
                <Box
                  component='button'
                  onClick={handleResetFilters}
                  sx={{
                    border: 'none',
                    bgcolor: 'transparent',
                    color: 'error.main',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'error.lighter' },
                  }}
                >
                  <Iconify icon='solar:close-circle-bold' width={14} />
                  Limpar
                </Box>
              )}
            </Stack>
            <OrderTableToolbar
              filters={filters}
              onFilters={handleFilters}
              onExportExcel={handleExportExcel}
              onExportPDF={handleExportPDF}
              hasData={data?.data && data.data.length > 0}
            />
          </Card>

          {!filters.event ? (
            <SelectEventPlaceholder />
          ) : (
            <>
              <Grid container spacing={2}>
                <Grid xs={12} sm={6} md={4}>
                  <MetricCard
                    title='Total de Vendas'
                    value={formatCurrency(extraData.totalValue || 0)}
                    icon='solar:dollar-minimalistic-bold'
                    color='success'
                    subtitle={`${data?.total || 0} pedidos realizados`}
                    isLoading={isLoadingData}
                  />
                </Grid>
                <Grid xs={12} sm={6} md={4}>
                  <MetricCard
                    title='Total de Taxas'
                    value={formatCurrency(extraData.totalTax || 0)}
                    icon='solar:calculator-minimalistic-bold'
                    color='warning'
                    subtitle='Taxas de cartão aplicadas'
                    isLoading={isLoadingData}
                  />
                </Grid>
                <Grid xs={12} sm={6} md={4}>
                  <MetricCard
                    title='Valor Líquido'
                    value={formatCurrency(extraData.organizedTax || 0)}
                    icon='solar:wallet-money-bold'
                    color='primary'
                    subtitle='Valor após taxas'
                    isLoading={isLoadingData}
                  />
                </Grid>
              </Grid>

              <PaymentTypeBreakdown extraData={extraData} />

              <AnalyticsCrossLink eventId={filters.event?.value as string | undefined} />

              <Card
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  flexWrap='wrap'
                  gap={1.5}
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                  }}
                >
                  <Stack direction='row' alignItems='center' gap={1.25}>
                    <Iconify
                      icon='solar:list-check-bold'
                      width={18}
                      sx={{ color: 'success.main' }}
                    />
                    <Typography variant='subtitle2' fontWeight={700} color='text.primary'>
                      Lista de Vendas
                    </Typography>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ display: { xs: 'none', md: 'inline' } }}
                    >
                      · Histórico detalhado de todas as transações
                    </Typography>
                  </Stack>
                  <Stack direction='row' alignItems='center' gap={0.75}>
                    <Chip
                      label={`${data?.total || 0} vendas`}
                      size='small'
                      color='success'
                      variant='soft'
                      sx={{ fontWeight: 600, height: 22 }}
                    />
                    <Chip
                      label='Tempo real'
                      size='small'
                      color='info'
                      variant='soft'
                      icon={<Iconify icon='solar:refresh-bold' width={12} />}
                      sx={{ height: 22 }}
                    />
                  </Stack>
                </Stack>

                {canReset && (
                  <OrderTableFiltersResult
                    filters={filters}
                    onFilters={handleFilters}
                    onResetFilters={handleResetFilters}
                    results={data?.data.length || 0}
                    sx={{ p: 2, pt: 1 }}
                  />
                )}

                <Datagrid
                  data={data?.data || []}
                  notFound={notFound}
                  noSelection
                  isLoading={isLoadingData}
                  headLabel={TABLE_HEAD}
                  refetch={refetch}
                  total={data?.total || 0}
                  setPagination={setPagination}
                  pagination={pagination}
                  ItemContent={OrderTableRow}
                  defaultDense
                  rowsPerPageOptions={[8, 15, 32, 50, 100, 500, 1000, 5000]}
                />
              </Card>
            </>
          )}
        </Stack>
      </Container>
    </>
  )
}

// ---------------------------------------------------------------------------

function SelectEventPlaceholder() {
  return (
    <Card
      sx={{
        p: { xs: 4, md: 8 },
        borderRadius: 3,
        textAlign: 'center',
        background:
          'linear-gradient(135deg, rgba(99, 102, 241, 0.04) 0%, rgba(139, 92, 246, 0.04) 100%)',
        border: '2px dashed',
        borderColor: 'primary.light',
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          bgcolor: 'primary.lighter',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 3,
        }}
      >
        <Iconify icon='solar:calendar-search-bold' width={40} color='primary.main' />
      </Box>
      <Typography variant='h5' fontWeight='bold' color='text.primary' gutterBottom>
        Selecione um evento
      </Typography>
      <Typography
        variant='body1'
        color='text.secondary'
        sx={{ maxWidth: 480, mx: 'auto', mb: 2 }}
      >
        Para visualizar as vendas, utilize o filtro acima e selecione o evento desejado.
      </Typography>
      <Typography variant='body2' color='text.disabled'>
        Os dados de vendas, métricas e relatórios serão exibidos após a seleção.
      </Typography>
    </Card>
  )
}

// ---------------------------------------------------------------------------

function AnalyticsCrossLink({ eventId }: { eventId?: string }) {
  return (
    <Paper
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'primary.lighter',
        background:
          'linear-gradient(135deg, rgba(99, 102, 241, 0.06) 0%, rgba(168, 85, 247, 0.06) 100%)',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            flexShrink: 0,
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
          }}
        >
          <Iconify icon='solar:chart-2-bold' width={22} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction='row' alignItems='center' spacing={0.75} flexWrap='wrap'>
            <Typography
              variant='subtitle2'
              fontWeight={700}
              color='text.primary'
              sx={{ fontSize: 14 }}
            >
              Quer ver mais detalhes por produto?
            </Typography>
            <Chip
              label='Novo'
              size='small'
              color='success'
              variant='soft'
              sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
            />
          </Stack>
          <Typography variant='body2' color='text.secondary' sx={{ fontSize: 13, mt: 0.25 }}>
            Veja ranking de produtos, mapa de calor por horário, vendas por categoria e muito mais
            na <strong>Análise de Produtos</strong>.
          </Typography>
        </Box>

        <Button
          component={RouterLink}
          to={`${paths.dashboard.productsAnalysis}${eventId ? `?event=${eventId}` : ''}`}
          variant='contained'
          size='small'
          color='success'
          endIcon={<Iconify icon='eva:arrow-ios-forward-fill' width={16} />}
          sx={{
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 1.5,
            px: 2,
            flexShrink: 0,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4)',
            },
          }}
        >
          Ver Análise de Produtos
        </Button>
      </Stack>
    </Paper>
  )
}

// ---------------------------------------------------------------------------

interface DashboardHeaderProps {
  autoRefresh: boolean
  setAutoRefresh: (v: boolean) => void
  isLoadingData: boolean
  handleRefresh: () => void
  hasEvent: boolean
  data: any
  extraData: any
}

function DashboardHeader(props: DashboardHeaderProps) {
  const { autoRefresh, setAutoRefresh, isLoadingData, handleRefresh, hasEvent, data, extraData } =
    props

  const kpis = hasEvent
    ? [
        { label: 'Vendas', value: data?.total || 0 },
        { label: 'Faturamento', value: formatCurrency(extraData.totalValue || 0) },
        {
          label: 'Taxa Média',
          value: `${
            extraData.totalValue > 0
              ? Math.round((extraData.totalTax / extraData.totalValue) * 100)
              : 0
          }%`,
        },
        {
          label: 'Ticket Médio',
          value:
            (data?.total || 0) > 0
              ? formatCurrency((extraData.totalValue || 0) / (data?.total || 1))
              : 'R$ 0,00',
        },
      ]
    : []

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 2,
        px: { xs: 2, sm: 2.5 },
        py: { xs: 1.5, sm: 2 },
        mb: { xs: 2, md: 3 },
        color: 'white',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent='space-between'
        spacing={{ xs: 1.5, md: 2 }}
      >
        <Stack direction='row' alignItems='center' spacing={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: 'rgba(255, 255, 255, 0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Iconify icon='solar:chart-square-bold' width={20} />
          </Box>
          <Box>
            <Typography
              variant='subtitle1'
              fontWeight={700}
              sx={{ fontSize: { xs: '1rem', sm: '1.125rem' }, lineHeight: 1.2 }}
            >
              Dashboard de Vendas
            </Typography>
            <Typography
              variant='caption'
              sx={{
                opacity: 0.85,
                fontSize: '0.75rem',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Acompanhe suas vendas em tempo real
            </Typography>
          </Box>
        </Stack>

        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
          <Stack
            direction='row'
            alignItems='center'
            spacing={0.75}
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: 1.5,
              bgcolor: 'rgba(255, 255, 255, 0.12)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: autoRefresh || isLoadingData ? '#10b981' : '#9ca3af',
                animation:
                  autoRefresh || isLoadingData
                    ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                    : 'none',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                },
              }}
            />
            <Typography
              variant='caption'
              sx={{ fontWeight: 500, fontSize: '0.7rem', opacity: 0.9 }}
            >
              {(() => {
                if (isLoadingData) return 'Atualizando...'
                return autoRefresh ? 'Auto (30s)' : 'Manual'
              })()}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  size='small'
                  sx={{
                    '& .MuiSwitch-thumb': { bgcolor: 'white' },
                    '& .MuiSwitch-track': { bgcolor: 'rgba(255, 255, 255, 0.3)' },
                    '& .Mui-checked .MuiSwitch-thumb': { bgcolor: '#10b981' },
                    '& .Mui-checked + .MuiSwitch-track': {
                      bgcolor: 'rgba(16, 185, 129, 0.5)',
                    },
                  }}
                />
              }
              label=''
              sx={{ m: 0 }}
            />
          </Stack>

          <Box
            component='button'
            onClick={handleRefresh}
            disabled={isLoadingData}
            sx={{
              px: 1.5,
              py: 0.75,
              borderRadius: 1.5,
              bgcolor: 'rgba(255, 255, 255, 0.18)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.28)' },
              '&:disabled': { opacity: 0.6, cursor: 'not-allowed' },
            }}
          >
            <Iconify
              icon='solar:refresh-bold'
              width={14}
              sx={{
                animation: isLoadingData ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
            <Typography variant='caption' fontWeight={500} sx={{ fontSize: '0.7rem' }}>
              Atualizar
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {hasEvent && (
        <Grid container spacing={1.5} mt={1.5}>
          {kpis.map((item) => (
            <Grid xs={6} md={3} key={item.label}>
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                <Typography
                  variant='caption'
                  sx={{
                    opacity: 0.85,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  variant='h6'
                  fontWeight={700}
                  sx={{
                    fontSize: { xs: '0.95rem', sm: '1.125rem' },
                    lineHeight: 1.2,
                    mt: 0.25,
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  )
}

// ---------------------------------------------------------------------------

function PaymentTypeBreakdown({ extraData }: { extraData: any }) {
  if (!extraData.paymentTypeValues && !extraData.tax) return null

  const paymentTypes: Record<string, { label: string; icon: string; color: string }> = {
    money: { label: 'Dinheiro', icon: 'solar:banknote-2-bold', color: 'success' },
    credit: { label: 'Crédito', icon: 'solar:card-send-bold', color: 'error' },
    debit: { label: 'Débito', icon: 'solar:card-2-bold-duotone', color: 'info' },
    pix: { label: 'PIX', icon: 'solar:qr-code-bold', color: 'warning' },
  }

  return (
    <Card sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
      <Stack direction='row' alignItems='center' gap={1.5} mb={2.5}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Iconify icon='solar:card-bold' width={20} color='white' />
        </Box>
        <Typography variant='subtitle1' fontWeight='bold' color='text.primary'>
          Vendas por Tipo de Pagamento
        </Typography>
      </Stack>
      <Grid container spacing={2}>
        {Object.entries(extraData.paymentTypeValues || extraData.tax || {})
          .filter(([type]) => type !== 'card')
          .map(([type, amount]: [string, any]) => {
            const config = paymentTypes[type] || {
              label: type,
              icon: 'solar:card-bold',
              color: 'primary',
            }
            const grossValue = Number(amount) || 0
            const taxValue = Number(extraData.tax?.[type]) || 0
            const netValue = grossValue - taxValue

            return (
              <Grid xs={12} sm={6} md={3} key={type}>
                <Paper
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: `${config.color}.light`,
                    bgcolor: 'background.paper',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: (theme) => theme.shadows[4],
                    },
                  }}
                >
                  <Stack direction='row' alignItems='center' gap={1} mb={1.5}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: `${config.color}.lighter`,
                        color: `${config.color}.main`,
                      }}
                    >
                      <Iconify icon={config.icon} width={18} />
                    </Box>
                    <Typography variant='subtitle2' fontWeight={600}>
                      {config.label}
                    </Typography>
                  </Stack>

                  <Stack spacing={0.75}>
                    <Stack direction='row' justifyContent='space-between' alignItems='center'>
                      <Typography variant='caption' color='text.secondary'>
                        Total Bruto
                      </Typography>
                      <Typography variant='subtitle2' fontWeight={700}>
                        {formatCurrency(grossValue)}
                      </Typography>
                    </Stack>
                    <Stack direction='row' justifyContent='space-between' alignItems='center'>
                      <Typography variant='caption' color='error.main'>
                        Taxa descontada
                      </Typography>
                      <Typography variant='caption' fontWeight={600} color='error.main'>
                        - {formatCurrency(taxValue)}
                      </Typography>
                    </Stack>
                    <Box sx={{ borderTop: '1px dashed', borderColor: 'divider', pt: 0.75 }}>
                      <Stack direction='row' justifyContent='space-between' alignItems='center'>
                        <Typography variant='caption' color='success.dark' fontWeight={600}>
                          Valor Líquido
                        </Typography>
                        <Typography variant='subtitle2' fontWeight={700} color='success.dark'>
                          {formatCurrency(netValue)}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            )
          })}
      </Grid>
    </Card>
  )
}
